import { useRef, useState, type PointerEvent as RPointerEvent } from "react";
import { GRADIENT_PRESETS, gradientCss, hexAt, type GradientAxis, type GradientStop } from "../gradient";

const AXES: GradientAxis[] = ["x", "y", "z", "radial"];

/**
 * A multi-stop gradient editor: drag the stops along the bar, click the bar to
 * add one, recolour or remove the selected stop, and pick which axis of the
 * shape the gradient runs along. Presets underneath. Emits the stop array up;
 * the swarm re-colours live.
 */
export function GradientEditor({
  stops, onChange, axis, onAxis,
}: {
  stops: GradientStop[];
  onChange: (s: GradientStop[]) => void;
  axis: GradientAxis;
  onAxis: (a: GradientAxis) => void;
}) {
  const barRef = useRef<HTMLDivElement>(null);
  const dragging = useRef<number | null>(null);
  const [sel, setSel] = useState(0);
  const selStop = stops[sel] ?? stops[0];

  const tFromX = (clientX: number) => {
    const r = barRef.current?.getBoundingClientRect();
    if (!r) return 0;
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
  };

  const startDrag = (e: RPointerEvent, i: number) => {
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragging.current = i;
    setSel(i);
  };
  const onMove = (e: RPointerEvent, i: number) => {
    if (dragging.current !== i) return;
    const next = stops.map((s, k) => (k === i ? { ...s, t: tFromX(e.clientX) } : s));
    onChange(next);
  };
  const endDrag = (e: RPointerEvent) => {
    dragging.current = null;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
  };

  const addStop = (e: RPointerEvent) => {
    const t = tFromX(e.clientX);
    onChange([...stops, { t, color: hexAt(stops, t) }]);
    setSel(stops.length);
  };
  const setColor = (hex: string) => onChange(stops.map((s, k) => (k === sel ? { ...s, color: hex } : s)));
  const removeSel = () => {
    if (stops.length <= 2) return;
    onChange(stops.filter((_, k) => k !== sel));
    setSel(0);
  };

  return (
    <div className="grad-editor">
      <div className="grad-bar" ref={barRef} style={{ background: gradientCss(stops) }} onPointerDown={addStop} title="click to add a stop">
        {stops.map((s, i) => (
          <button
            key={i}
            className={"grad-stop" + (i === sel ? " sel" : "")}
            style={{ left: `${s.t * 100}%`, background: s.color }}
            onPointerDown={(e) => startDrag(e, i)}
            onPointerMove={(e) => onMove(e, i)}
            onPointerUp={endDrag}
            title="drag to move"
          />
        ))}
      </div>
      <div className="grad-controls">
        <label className="color-swatch grad-swatch" title="stop colour" style={{ background: selStop?.color }}>
          <input type="color" value={selStop?.color ?? "#ffffff"} onChange={(e) => setColor(e.target.value)} />
        </label>
        <button className="mini" onClick={removeSel} disabled={stops.length <= 2}>− stop</button>
        <span className="grad-axis-label">axis</span>
        <span className="seg">
          {AXES.map((a) => (
            <button key={a} className={"chip seg-btn mini-seg" + (axis === a ? " active" : "")} onClick={() => onAxis(a)}>{a}</button>
          ))}
        </span>
      </div>
      <div className="grad-presets">
        {GRADIENT_PRESETS.map((p) => (
          <button
            key={p.name}
            className="grad-preset"
            style={{ background: gradientCss(p.stops) }}
            title={p.name}
            onClick={() => { onChange(p.stops.map((s) => ({ ...s }))); setSel(0); }}
          />
        ))}
      </div>
    </div>
  );
}
